/** @jsxImportSource @emotion/react */

import { jsonToCsv } from '@quickbit/backoffice-core-frontend';
import {
  MoneyScaled,
  removeScaleFromMoney,
  UtilityButton,
} from '@quickbit/qb-design-system';
import { useGetCorporateWalletTransactions } from 'api/walletApi';
import { type CorporateWalletTransactionMinimal } from 'api/walletApi.types';
import { formatCurrency } from 'utils';
import { mapTransactionTypeToText } from './Transactions/components/utils';

interface Props {
  merchantId: string;
  disabled?: boolean;
}
const columns = [
  'Created At',
  'Transaction Id',
  'Blockchain Hash',
  'Type',
  'Amount',
  'Amount (EUR)',
  'Balance Before',
  'Balance Before (EUR)',
  'Balance After',
  'Balance After (EUR)',
  'Fee (EUR)',
  'Initiated By',
];
type Columns = (typeof columns)[number];

type Row = Record<Columns, string | number | MoneyScaled | null | undefined>;

const corporateWalletTransactionToRow = (
  transaction: CorporateWalletTransactionMinimal
): Row => {
  return {
    'Created At': new Date(transaction.createdAt).toDateString(),
    'Transaction Id': transaction.id,
    'Blockchain Hash': transaction.blockchainTransactionId ?? '-',
    Type: mapTransactionTypeToText(transaction.type),
    Amount: formatCurrency(transaction.amount),
    'Amount (EUR)': removeScaleFromMoney(transaction.amountReference),
    'Balance Before': transaction.balanceAtTransactionTime
      ? formatCurrency(transaction.balanceAtTransactionTime)
      : '-',
    'Balance Before (EUR)': transaction.balanceReferenceAtTransactionTime
      ? removeScaleFromMoney(transaction.balanceReferenceAtTransactionTime)
      : '-',
    'Balance After': transaction.balanceAfterTransaction
      ? formatCurrency(transaction.balanceAfterTransaction)
      : '-',
    'Balance After (EUR)': transaction.balanceReferenceAfterTransaction
      ? removeScaleFromMoney(transaction.balanceReferenceAfterTransaction)
      : '-',
    'Fee (EUR)': removeScaleFromMoney(transaction.referenceFee),
    'Initiated By': transaction.authorizedBy?.email,
  };
};

export const ExportCsv = ({ merchantId, disabled }: Props) => {
  const { data: transactionsData, isLoading: transactionsLoading } =
    useGetCorporateWalletTransactions(merchantId);

  const downloadCsv = () => {
    if (!transactionsData) {
      return [];
    }
    const transactions = transactionsData.transactions;

    const data: Row[] = transactions.map((transaction) =>
      corporateWalletTransactionToRow(transaction)
    );

    jsonToCsv({
      data: data,
      filename: 'CorporateWalletTransactions',
      delimiter: ';',
      columns: columns,
    });
    return data;
  };

  return (
    <UtilityButton
      text="Download"
      onClick={downloadCsv}
      loading={transactionsLoading}
      disabled={disabled || transactionsLoading}
    />
  );
};
